/**
 * Cross-tab-friendly sound effects.
 *
 * WebAudio (`AudioContext`) gets aggressively suspended in background tabs
 * so the huddle chime never actually plays when you switch to Gmail or
 * WhatsApp Web. HTMLAudioElement with an inline data-URI works around this:
 * once loaded (which we do at module import), the browser can play the clip
 * even when the tab is hidden — the same way Slack / Discord notify you.
 *
 * Each clip is a short PCM WAV encoded inline so we don't ship an mp3 asset
 * and don't need CORS gymnastics for the object store.
 *
 * Usage:
 *   import { playIncomingCall, playMessagePing } from "@/lib/soundEffects";
 *   playIncomingCall();
 */

// ------------ WAV encoder (mono, 16-bit PCM) ------------
function _makeWav(samples, sampleRate = 22050) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (offset, s) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);        // PCM
  view.setUint16(22, 1, true);        // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

function _bufToDataUri(buffer) {
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return "data:audio/wav;base64," + btoa(bin);
}

// A calm two-note bell (E5 → A5) — same feel as WhatsApp / Gather Town.
function _buildIncomingCall() {
  const sr = 22050;
  const dur = 0.75;
  const n = Math.floor(sr * dur);
  const out = new Float32Array(n);
  const note = (t, freq, start, len) => {
    if (t < start || t > start + len) return 0;
    const local = t - start;
    const env = Math.exp(-3 * local) * (1 - Math.exp(-30 * local));
    return env * Math.sin(2 * Math.PI * freq * local);
  };
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    out[i] = 0.45 * (note(t, 659.25, 0.0, 0.4) + note(t, 880.0, 0.16, 0.6));
  }
  return _bufToDataUri(_makeWav(out, sr));
}

// A short single-note ping (C6, ~120 ms) — used for new chat messages.
function _buildMessagePing() {
  const sr = 22050;
  const dur = 0.22;
  const n = Math.floor(sr * dur);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const env = Math.exp(-14 * t) * (1 - Math.exp(-80 * t));
    out[i] = 0.35 * env * Math.sin(2 * Math.PI * 1046.5 * t);
  }
  return _bufToDataUri(_makeWav(out, sr));
}

// ------------ Preloaded elements (created lazily on first user gesture) ------------
let _callAudio = null;
let _msgAudio = null;
let _unlocked = false;

function _get(kind) {
  if (kind === "call") {
    if (!_callAudio) {
      _callAudio = new Audio(_buildIncomingCall());
      _callAudio.preload = "auto";
      _callAudio.volume = 0.7;
    }
    return _callAudio;
  }
  if (!_msgAudio) {
    _msgAudio = new Audio(_buildMessagePing());
    _msgAudio.preload = "auto";
    _msgAudio.volume = 0.35;
  }
  return _msgAudio;
}

/**
 * Browser autoplay policies block sound until the user has interacted with
 * the page at least once. This helper is called on the first click / key so
 * subsequent programmatic `play()` calls (including from background tabs)
 * are allowed. Silent 0-volume plays "unlock" the audio element without
 * making noise.
 */
export function unlockSounds() {
  if (_unlocked) return;
  _unlocked = true;
  try {
    for (const kind of ["call", "message"]) {
      const a = _get(kind);
      const prev = a.volume;
      a.volume = 0;
      const p = a.play();
      if (p && typeof p.then === "function") {
        p.then(() => { a.pause(); a.currentTime = 0; a.volume = prev; })
         .catch(() => { a.volume = prev; });
      } else {
        a.pause(); a.currentTime = 0; a.volume = prev;
      }
    }
  } catch { /* ignore — worst case sounds are silent, no functional break */ }
}

function _play(kind) {
  try {
    const a = _get(kind);
    a.currentTime = 0;
    const p = a.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch { /* ignore */ }
}

export function playIncomingCall() { _play("call"); }
export function playMessagePing() { _play("message"); }

// Auto-arm the unlock on the very first user interaction anywhere in the app.
if (typeof window !== "undefined") {
  const arm = () => {
    unlockSounds();
    window.removeEventListener("click", arm);
    window.removeEventListener("keydown", arm);
    window.removeEventListener("touchstart", arm);
  };
  window.addEventListener("click", arm, { once: true });
  window.addEventListener("keydown", arm, { once: true });
  window.addEventListener("touchstart", arm, { once: true });
}
